import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header';
import { Footer } from './components/Footer';
import styles from './LegalPages.module.css';
import { FileText, Shield, AlertCircle } from 'lucide-react';

const TermsOfService = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    return (
        <div className={ styles.container }>
            <Header />

            <main className={ styles.main }>
                {/* Hero Section */ }
                <section className={ styles.hero }>
                    <div className={ styles.heroContent }>
                        <div className={ styles.iconBox }>
                            <FileText size={ 48 } />
                        </div>
                        <h1 className={ styles.title }>Terms of Service</h1>
                        <p className={ styles.lastUpdated }>Last updated: 25 February 2026</p>
                    </div>
                </section>

                {/* Content Section */ }
                <section className={ styles.content }>
                    <div className={ styles.contentWrapper }>
                        {/* Introduction */ }
                        <div className={ styles.introBox }>
                            <AlertCircle size={ 24 } />
                            <div>
                                <p>
                                    These Terms of Service ("Terms") constitute a legally binding agreement between you ("you," "your," or "Customer") and Galile Technologies Private Limited ("Galile," "we," "us," or "our") governing your access to and use of the website (e.g., galile.io), platform, applications, APIs, add-ons, integrations, products, and any other services we provide (collectively, the "Service").
                                </p>
                                <p>
                                    By accessing or using the Service, clicking "I agree," or creating an account, you agree to these Terms. If you are agreeing on behalf of an organization, you represent that you are authorized to bind that organization.
                                </p>
                            </div>
                        </div>

                        {/* Table of Contents */ }
                        <div className={ styles.tocBox }>
                            <h3>Table of Contents</h3>
                            <div className={ styles.tocGrid }>
                                <a href="#definitions">1. Definitions</a>
                                <a href="#eligibility">2. Eligibility</a>
                                <a href="#license">3. Service License</a>
                                <a href="#account">4. Account Registration & Security</a>
                                <a href="#fees">5. Subscriptions, Fees & Payments</a>
                                <a href="#acceptable-use">6. Acceptable Use Policy</a>
                                <a href="#data">7. Customer Data & Privacy</a>
                                <a href="#security">8. Security & Data Protection</a>
                                <a href="#publicity">9. Publicity and Marketing Rights</a>
                                <a href="#third-party">10. Third-Party Services</a>
                                <a href="#feedback">11. Feedback & Suggestions</a>
                                <a href="#warranty">12. Warranty Disclaimer</a>
                                <a href="#liability">13. Limitation of Liability</a>
                                <a href="#indemnification">14. Indemnification</a>
                                <a href="#confidentiality">15. Confidentiality</a>
                                <a href="#termination">16. Term & Termination</a>
                                <a href="#compliance">17. Export, Sanctions & Compliance</a>
                                <a href="#governing-law">18. Governing Law & Dispute Resolution</a>
                                <a href="#miscellaneous">19. Miscellaneous</a>
                                <a href="#contact">20. Contact Information</a>
                            </div>
                        </div>

                        {/* Section 1 */ }
                        <div id="definitions" className={ styles.section }>
                            <h2>1. DEFINITIONS</h2>
                            <div className={ styles.definitionList }>
                                <div className={ styles.definition }>
                                    <strong>Account:</strong> Your registered user account to access the Service.
                                </div>
                                <div className={ styles.definition }>
                                    <strong>Affiliate:</strong> Any entity that directly or indirectly controls, is controlled by, or is under common control with a party.
                                </div>
                                <div className={ styles.definition }>
                                    <strong>Customer Data:</strong> Any data, recordings, transcripts, text, or content submitted or generated by Customer through the Service.
                                </div>
                                <div className={ styles.definition }>
                                    <strong>Documentation:</strong> User guides, technical documentation, and developer guides provided by Galile.
                                </div>
                            </div>
                        </div>

                        {/* Section 2 */ }
                        <div id="eligibility" className={ styles.section }>
                            <h2>2. ELIGIBILITY</h2>
                            <p>
                                You must be at least 18 years old and legally capable of entering binding contracts to use the Service. You may not use the Service if prohibited by applicable law.
                            </p>
                        </div>

                        {/* Section 3 */ }
                        <div id="license" className={ styles.section }>
                            <h2>3. SERVICE LICENSE</h2>
                            <p>
                                Subject to these Terms and payment of applicable fees, Galile grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Service solely for your internal business purposes.
                            </p>
                            <p><strong>You agree not to:</strong></p>
                            <ul>
                                <li>Copy, modify, or create derivative works of the Service</li>
                                <li>Reverse engineer, decompile, or disassemble</li>
                                <li>Remove proprietary notices</li>
                                <li>Use the Service to build a competing product</li>
                            </ul>
                        </div>

                        {/* Section 4 */ }
                        <div id="account" className={ styles.section }>
                            <h2>4. ACCOUNT REGISTRATION & SECURITY</h2>
                            <p><strong>You are responsible for:</strong></p>
                            <ul>
                                <li>Providing accurate and current registration information</li>
                                <li>Maintaining the confidentiality of your login credentials</li>
                                <li>Restricting access to your Account</li>
                                <li>All activity under your Account</li>
                            </ul>
                            <p>You must notify Galile immediately of unauthorized access or suspicious activity.</p>
                        </div>

                        {/* Section 5 */ }
                        <div id="fees" className={ styles.section }>
                            <h2>5. SUBSCRIPTIONS, FEES & PAYMENTS</h2>

                            <h3>5.1 Subscription Plans</h3>
                            <p>The Service is offered on a subscription basis with fees and billing terms set out during signup or in a written agreement.</p>

                            <h3>5.2 Billing & Renewal</h3>
                            <ul>
                                <li>Subscriptions renew automatically unless cancelled in advance.</li>
                                <li>Fees are charged in advance on the billing date.</li>
                                <li>You authorize Galile to charge your payment method.</li>
                            </ul>

                            <h3>5.3 Taxes</h3>
                            <p>You are responsible for all applicable taxes, duties, and levies.</p>

                            <h3>5.4 Refunds</h3>
                            <p>Unless otherwise stated, fees paid are non-refundable.</p>

                            <h3>5.5 Suspension for Non-Payment</h3>
                            <p>Galile may suspend access if payments are overdue.</p>
                        </div>

                        {/* Section 6 */ }
                        <div id="acceptable-use" className={ styles.section }>
                            <h2>6. ACCEPTABLE USE POLICY</h2>
                            <p><strong>You agree not to use the Service to:</strong></p>
                            <ul>
                                <li>Violate laws or regulations</li>
                                <li>Upload malware, spam, or harmful code</li>
                                <li>Record meetings without required consent</li>
                                <li>Interfere with the Service or disrupt infrastructure</li>
                                <li>Access other accounts without permission</li>
                            </ul>
                            <p>Galile may investigate violations and may suspend or terminate Accounts.</p>
                        </div>

                        {/* Section 7 */ }
                        <div id="data" className={ styles.section }>
                            <h2>7. CUSTOMER DATA & PRIVACY</h2>

                            <h3>7.1 Customer Data Ownership</h3>
                            <p>You retain ownership of all Customer Data.</p>

                            <h3>7.2 License to Galile</h3>
                            <p>You grant Galile a limited license to:</p>
                            <ul>
                                <li>Host, store, and process data</li>
                                <li>Provide transcripts, analytics, and insights</li>
                                <li>Improve and operate the Service</li>
                            </ul>
                            <p>Galile does not claim ownership over your Customer Data.</p>

                            <h3>7.3 Privacy Policy</h3>
                            <p>Processing of personal data is governed by the <a href="/privacy-policy" onClick={ (e) => { e.preventDefault(); navigate('/privacy-policy'); } }>Privacy Policy</a>.</p>
                        </div>

                        {/* Section 8 */ }
                        <div id="security" className={ styles.section }>
                            <h2>8. SECURITY & DATA PROTECTION</h2>
                            <p>Galile implements reasonable administrative, technical, and organizational safeguards, including:</p>
                            <ul>
                                <li>Encryption in transit and at rest</li>
                                <li>Access controls</li>
                                <li>Monitoring and logging</li>
                            </ul>
                            <p>However, no system is completely secure. Galile disclaims liability for unauthorized access not resulting from its negligence.</p>
                        </div>

                        {/* Section 9 - Publicity */ }
                        <div id="publicity" className={ styles.section }>
                            <h2>9. PUBLICITY AND MARKETING RIGHTS</h2>
                            <p>
                                Unless otherwise agreed in writing, Customer grants Galile a limited, non-exclusive, worldwide, royalty-free right to use Customer's name, logo, and general description of the Customer's use of the Service for Galile's marketing and promotional purposes, including on its website, sales materials, presentations, and customer lists.
                            </p>
                            <p>
                                Galile will use such name and logo in accordance with any publicly available brand guidelines provided by Customer.
                            </p>
                            <p>
                                Customer may revoke this right at any time by providing written notice to Galile, after which Galile will cease future use within a reasonable period.
                            </p>
                        </div>

                        {/* Section 10 (was 9) */ }
                        <div id="third-party" className={ styles.section }>
                            <h2>10. THIRD-PARTY SERVICES</h2>
                            <p>
                                The Service may integrate with third-party tools (e.g., video conferencing, email, CRM, storage). You agree that Galile is not responsible for third-party availability, performance, or policies. Third-party terms may apply.
                            </p>
                        </div>

                        {/* Section 11 (was 10) */ }
                        <div id="feedback" className={ styles.section }>
                            <h2>11. FEEDBACK & SUGGESTIONS</h2>
                            <p>
                                If you provide feedback, enhancements, or suggestions ("Feedback"), you grant Galile a worldwide, perpetual, irrevocable, royalty-free license to use and implement Feedback without compensation.
                            </p>
                        </div>

                        {/* Section 12 (was 11) */ }
                        <div id="warranty" className={ styles.section }>
                            <h2>12. WARRANTY DISCLAIMER</h2>
                            <div className={ styles.warningBox }>
                                <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
                                <p>
                                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." GALILE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:
                                </p>
                                <ul>
                                    <li>MERCHANTABILITY</li>
                                    <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                                    <li>NON-INFRINGEMENT</li>
                                    <li>UNINTERRUPTED OR ERROR-FREE SERVICE</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 13 (was 12) */ }
                        <div id="liability" className={ styles.section }>
                            <h2>13. LIMITATION OF LIABILITY</h2>
                            <div className={ styles.warningBox }>
                                <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
                                <p>
                                    GALILE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY DAMAGES.
                                </p>
                                <p>
                                    GALILE'S TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE TOTAL AMOUNT YOU PAID TO GALILE IN THE 12 MONTHS PRECEDING THE CLAIM.
                                </p>
                                <p>This section survives termination.</p>
                            </div>
                        </div>

                        {/* Section 14 (was 13) */ }
                        <div id="indemnification" className={ styles.section }>
                            <h2>14. INDEMNIFICATION</h2>
                            <p>You agree to indemnify, defend, and hold harmless Galile, its Affiliates, officers, and employees from claims arising out of:</p>
                            <ul>
                                <li>Your use of the Service</li>
                                <li>Violation of the Terms</li>
                                <li>Misuse of meeting recordings</li>
                                <li>Data submitted in violation of law</li>
                            </ul>
                        </div>

                        {/* Section 15 (was 14) */ }
                        <div id="confidentiality" className={ styles.section }>
                            <h2>15. CONFIDENTIALITY</h2>
                            <p>
                                Both parties agree to keep confidential any non-public information disclosed during the relationship, and use such information only to perform obligations under these Terms.
                            </p>
                        </div>

                        {/* Section 16 (was 15) */ }
                        <div id="termination" className={ styles.section }>
                            <h2>16. TERM & TERMINATION</h2>

                            <h3>16.1 Term</h3>
                            <p>These Terms commence when you first use the Service.</p>

                            <h3>16.2 Termination by You</h3>
                            <p>You may terminate by cancelling your subscription according to the billing terms.</p>

                            <h3>16.3 Termination by Galile</h3>
                            <p>Galile may terminate or suspend access for breach, non-payment, or legal obligation.</p>

                            <h3>16.4 Effects of Termination</h3>
                            <p>Upon termination:</p>
                            <ul>
                                <li>Your right to use the Service ends</li>
                                <li>Galile may delete Customer Data according to the Data Retention Policy</li>
                            </ul>
                        </div>

                        {/* Section 17 (was 16) */ }
                        <div id="compliance" className={ styles.section }>
                            <h2>17. EXPORT, SANCTIONS & COMPLIANCE</h2>
                            <p>
                                You agree not to use the Service in violation of export laws or sanctions. You represent that you are not located in a restricted territory.
                            </p>
                        </div>

                        {/* Section 18 (was 17) */ }
                        <div id="governing-law" className={ styles.section }>
                            <h2>18. GOVERNING LAW & DISPUTE RESOLUTION</h2>
                            <p>
                                This Agreement will be governed by the laws of India, without regard to its choice of law or conflicts of law principles. You and Galile consent to the exclusive jurisdiction and venue in the courts in India, except that temporary relief to enjoin infringement of intellectual property rights may be sought in any court where such infringement has occurred.
                            </p>
                            <p>
                                EACH PARTY WAIVES ANY OBJECTION (ON THE GROUNDS OF LACK OF JURISDICTION, FORUM NON CONVENIENS OR OTHERWISE) TO THE EXERCISE OF SUCH JURISDICTION OVER IT BY ANY SUCH COURTS.
                            </p>
                        </div>

                        {/* Section 19 (was 18) */ }
                        <div id="miscellaneous" className={ styles.section }>
                            <h2>19. MISCELLANEOUS</h2>
                            <ul>
                                <li><strong>Entire Agreement:</strong> These Terms and any policies are the entire agreement.</li>
                                <li><strong>Severability:</strong> If any provision is invalid, remaining provisions remain in effect.</li>
                                <li><strong>No Waiver:</strong> Failure to enforce a right does not waive future enforcement.</li>
                            </ul>
                        </div>

                        {/* Section 20 (was 19) */ }
                        <div id="contact" className={ styles.section }>
                            <h2>20. CONTACT INFORMATION</h2>
                            <p>
                                Please contact us by emailing to <a href="mailto:sagar@galile.io">sagar@galile.io</a> with any questions regarding these Terms of Service or the Services or to report any violations of these Terms of Service.
                            </p>
                        </div>

                        {/* Bottom CTA */ }
                        <div className={ styles.bottomCta }>
                            <Shield size={ 32 } />
                            <h3>Questions about our Terms?</h3>
                            <p>Our team is here to help you understand how we protect your business.</p>
                            <button onClick={ () => navigate('/contact') } className={ styles.ctaButton }>
                                Contact Us
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default TermsOfService;
